import type { FeatureIntroSlot } from 'reducers/playerPromos';

import type { ActiveTable } from 'components/SaloonLobby/types';

import type { FeatureIntroSeenState } from './featureIntroSeen';

/**
 * The pure rules behind when a one-time feature introduction may take the
 * session and when it may open: written for the multi-run introduction
 * (EN-10791) and shared with the heads-up Sit & Go one (EN-10748), whose
 * auto-prompts wire them to the store. Keeping them here keeps the rules
 * testable on their own, and one set of rules for every introduction.
 */

/**
 * Who gets this session's one login-modal slot (owner, 2026-09-24: never the
 * promo and an introduction both). Decided once per player per session.
 *   - no member identity           → 'none'   (guests never see it)
 *   - flags still evaluating        → 'pending' (the promo waits; bounded)
 *   - flag off                      → 'none'
 *   - already seen, or storage
 *     cannot be read (fail closed)  → 'none'
 *   - otherwise                     → 'reserved'
 */
export const decideIntroSlot = ({
  identity,
  flagsReady,
  flagOn,
  seen
}: {
  identity: string | null;
  flagsReady: boolean;
  flagOn: boolean;
  seen: FeatureIntroSeenState;
}): FeatureIntroSlot => {
  if (!identity) return 'none';
  if (!flagsReady) return 'pending';
  if (!flagOn) return 'none';
  if (seen !== 'unseen') return 'none';
  return 'reserved';
};

/**
 * Is the player in live play at any open table? An introduction never opens
 * over it, and closes if it starts: the backdrop would hide the dock digit, the
 * only cross-table cue for a turn, and a missed turn is an auto-fold.
 *
 * Live play = seated and either on the clock, or dealt into a hand still in
 * play. Folded, sitting out (no cards) and between-hands seats are not. A
 * table still awaiting its first payload has no hand phase to read, so dealt
 * cards there count as live: the safe side of "never over a hand".
 */
export const isInLivePlay = (tables: ActiveTable[]): boolean =>
  tables.some(
    (table) =>
      table.isSeated &&
      (table.isMyTurn ||
        ((table.isHandLive || table.awaitingPayload === true) &&
          !table.isFolded &&
          !table.handOver &&
          table.cards !== null))
  );

export type IntroOpenGates = {
  activeView: string;
  isLobbyReady: boolean;
  isLocationInvalid: boolean;
  biometricPromptActive: boolean;
  inLivePlay: boolean;
  promoEntryModalOpen: boolean;
};

/**
 * May the reserved introduction open right now? Lobby only (never a table or
 * the grid), once the lobby has loaded, with a valid location, after the
 * biometric enrollment offer has resolved, and never over live play.
 *
 * Never while the promo entry modal is open, either. The slot already keeps
 * the promo out of a session an introduction holds (login sets playerGUID
 * before promos are even requested, so the slot is decided first); this makes
 * "never both at once" hold even if that ordering ever changes.
 */
export const canOpenIntro = (gates: IntroOpenGates): boolean =>
  gates.activeView === 'lobby' &&
  gates.isLobbyReady &&
  !gates.isLocationInvalid &&
  !gates.biometricPromptActive &&
  !gates.inLivePlay &&
  !gates.promoEntryModalOpen;

/**
 * Must an OPEN introduction close without counting as seen? The same
 * conditions, minus the ones that cannot change underneath an open modal.
 * The biometric enrollment sheet can: its offer resolves when the profile
 * loads, which may be after the introduction opened, and the two must never
 * stack — the sheet takes the screen. A teardown ends this session's attempt
 * (no flicker back open); the player gets it at the next session.
 */
export const mustTearDownIntro = ({
  activeView,
  isLocationInvalid,
  inLivePlay,
  flagOn,
  biometricPromptOpen
}: {
  activeView: string;
  isLocationInvalid: boolean;
  inLivePlay: boolean;
  flagOn: boolean;
  biometricPromptOpen: boolean;
}): boolean =>
  activeView !== 'lobby' || isLocationInvalid || inLivePlay || !flagOn || biometricPromptOpen;

// The storage-backed half of the biometric gate does not notify, so an
// auto-prompt re-reads it on this clock while it is the only thing holding
// the modal back.
export const FEATURE_INTRO_BIOMETRIC_RECHECK_MS = 1000;

// How long a stored biometric offer may hold an introduction back. The offer
// resolves once the profile — and the email its credential is keyed by —
// loads, normally seconds after login. If the profile never loads, the stored
// flag survives to a later launch (useBiometricEnrollmentOffer), and without a
// ceiling it would hold the introduction, and with it the promo the reserved
// slot keeps out, for the rest of the session.
export const FEATURE_INTRO_BIOMETRIC_OFFER_WAIT_MS = 15000;
